<template>
  <FormSection
    :title="$t('form.invoiceForm.headers.invoice')"
    :image="require('~/images/ico-basic-info.svg')"
    class="el-form-item--lg"
    has-big-image
  >
    <Row spacing="md">
      <Column
        :xs="24"
        :md="12"
      >
        <FormGroup
          class="invoice-kind"
          prop="kind"
          :label="$t('form.invoiceForm.fields.kind.label')"
          :help="$t('form.invoiceForm.fields.kind.inputHelp')"
          floating-label
          has-input-value
        >
          <Select
            class="select-v2"
            name="kind"
            v-model="kind"
            filterable
            data-test="invoice_kind"
            :popper-append-to-body="false"
          >
            <OptionGroup
              v-for="(group, index) in groupedOptions"
              :key="index"
            >
              <Option
                v-for="option in group.options"
                :key="option.key"
                :label="option.value"
                :value="option.key"
                :data-test="`${option.key}_kind_option`"
              />
            </OptionGroup>
          </Select>
        </FormGroup>

        <FormGroup
          :error-message="numberInputError"
          :has-force-focus="showRegistrationNumberToggle"
          :help="numberInputHelp"
          :input-value="number"
          data-test="invoice_number_form"
          floating-label
          prop="number"
          ref="popoverReference"
          v-if="!recurring_draft"
        >
          <template #label-link>
            {{ $t('form.invoiceForm.fields.number.label') }}
            <span v-if="shouldShowComma">,&nbsp;</span>
            <div
              v-if="showRegistrationNumberToggle"
              class="flex-label"
            >
              <span class="registration-number_short-label"> {{ $t('form.invoiceForm.fields.registration_number.labelShort') }} </span>
              <span class="registration-number_label"> {{ $t('form.invoiceForm.fields.registration_number.label') }} </span>
              <Anchor
                class="anchor-toggle"
                data-test="registration_number_toggle"
                type="button"
                @click="toggleField('showRegistrationNumber')"
              >
                {{ $t('form.invoiceForm.buttons.enterAnotherOne') }}
              </Anchor>
              <span
                v-if="showPaymentSymbolToggle"
              >,&nbsp;</span>
            </div>
            <div v-if="showPaymentSymbolToggle">
              <span class="payment-symbol_label-short"> {{ $t('form.invoiceForm.fields.payment_symbol.labelShort') }} </span>
              <span class="payment-symbol_label"> {{ $t('form.invoiceForm.fields.payment_symbol.label') }} </span>
              <Anchor
                class="anchor-toggle"
                data-test="payment_symbol_toggle"
                type="button"
                @click="toggleField('showPaymentSymbol')"
              >
                {{ $t('form.invoiceForm.buttons.enterAnother') }}
              </Anchor>
            </div>
          </template>
          <TextInput
            name="number"
            data-test="invoice_number"
            v-model="number"
            @blur="onNumberBlur"
            @input="onNumberInput"
          />
        </FormGroup>

        <FormGroup
          v-if="settlementMethodEnabled"
          prop="kind"
          :label="$t('form.invoiceForm.fields.settlement_method.label')"
          floating-label
          :input-value="settlement_method"
        >
          <Select
            class="select-v2"
            name="settlement_method"
            v-model="settlement_method"
            filterable
            data-test="invoice_settlement_method"
            :popper-append-to-body="false"
          >
            <Option
              v-for="option in settlementMethodOptions"
              :key="option.key"
              :label="option.value"
              :value="option.key"
              :data-test="`${option.key}_settelment_method_option`"
            />
          </Select>
        </FormGroup>

        <FormGroup
          v-if="!settlementMethodEnabled && visibilities.registration_number && showRegistrationNumber && !recurring_draft"
          prop="registration_number"
          :label="$t('form.invoiceForm.fields.registration_number.label')"
          floating-label
          :input-value="registration_number"
          :tooltip-content="$t('form.invoiceForm.fields.registration_number.inputHelp')"
        >
          <TextInput
            ref="registrationNumberInput"
            name="registration_number"
            data-test="registration_number"
            type="string"
            v-model="registration_number"
            :help="$t('form.invoiceForm.fields.registration_number.inputHelp')"
          />
        </FormGroup>

        <FormGroup
          prop="reference"
          v-if="visibilities.reference"
          :label="$t('form.invoiceForm.fields.reference.label')"
          :help="$t('form.invoiceForm.fields.reference.inputHelp')"
          floating-label
          :input-value="reference"
        >
          <TextInput
            v-model="reference"
            name="reference"
            id="invoice_reference"
            data-test="invoice_reference"
          />
        </FormGroup>

        <div class="flex-row">
          <FormGroup
            prop="payment_symbol"
            v-if="showPaymentSymbol && visibilities.payment_symbol"
            :label="$t('form.invoiceForm.fields.payment_symbol.label')"
            :tooltip-content="$t('form.invoiceForm.fields.payment_symbol.inputHelp')"
            floating-label
            :input-value="payment_symbol"
          >
            <NumericInput
              ref="paymentSymbolInput"
              v-model="payment_symbol"
              name="payment_symbol"
              data-test="payment_symbol"
              class="input-no-arrows"
              id="invoice_payment_symbol"
            />
          </FormGroup>

          <FormGroup
            class="constant-symbol"
            prop="constant_symbol"
            :label="$t('form.invoiceForm.fields.constant_symbol.label')"
            v-if="showConstantSymbol && visibilities.constant_symbol"
            floating-label
            :input-value="constant_symbol"
          >
            <NumericInput
              ref="constantSymbolInput"
              v-model="constant_symbol"
              name="constant_symbol"
              data-test="constant_symbol"
              class="input-no-arrows"
              id="invoice_constant_symbol"
            >
              <span
                class="optional"
                slot="suffix"
              >
                {{ $t('form.invoiceForm.headers.optional') }}
              </span>
            </NumericInput>
          </FormGroup>

          <FormGroup
            class="specific-symbol"
            prop="specific_symbol"
            v-if="showSpecificSymbol && visibilities.specific_symbol"
            :label="$t('form.invoiceForm.fields.specific_symbol.label')"
            :tooltip-content="$t('form.invoiceForm.fields.specific_symbol.inputHelp')"
            floating-label
            :input-value="specific_symbol"
          >
            <NumericInput
              ref="specificSymbolInput"
              v-model="specific_symbol"
              name="specific_symbol"
              data-test="specific_symbol"
              class="input-no-arrows"
              id="invoice_specific_symbol"
            >
              <span
                class="optional"
                slot="suffix"
              >
                {{ $t('form.invoiceForm.headers.optional') }}
              </span>
            </NumericInput>
          </FormGroup>
        </div>

        <div
          class="custom-input-box"
        >
          <span
            class="constant-symbol_label"
            v-if="displayAlsoNeed"
          > {{ $t('form.invoiceForm.buttons.alsoNeed') }} </span>

          <Anchor
            v-if="showConstantSymbolToggle"
            class="anchor-toggle"
            data-test="constant_symbol_toggle"
            type="button"
            @click="toggleField('showConstantSymbol')"
          >
            {{ $t('form.invoiceForm.fields.constant_symbol.label') }}
          </Anchor>

          <span
            class="specific-symbol_label"
            v-if="displayOr"
          > {{ $t('form.invoiceForm.buttons.or') }} </span>

          <Anchor
            v-if="showSpecificSymbolToggle"
            class="anchor-toggle"
            data-test="specific_symbol_toggle"
            type="button"
            @click="toggleField('showSpecificSymbol')"
          >
            {{ $t('form.invoiceForm.fields.specific_symbol.label') }}
          </Anchor>
        </div>

        <FormGroup
          prop="issued_by"
          :label="$t('form.invoiceForm.fields.issued_by.label')"
          :help="$t('form.invoiceForm.fields.issued_by.inputHelp')"
          floating-label
          :input-value="issued_by"
        >
          <TextInput
            v-model="issued_by"
            name="issued_by"
            id="invoice_issued_by"
            data-test="invoice_issued_by"
          />
        </FormGroup>
      </Column>

      <Column
        class="column-right"
        :xs="24"
        :md="12"
      >
        <FormGroup
          :class="{ 'full-width': !visibilities.tax_point_on }"
          class="issued-on"
          prop="issued_on"
          :label="$t('form.invoiceForm.fields.issued_on.label')"
          floating-label
          :input-value="issued_on"
        >
          <DatePicker
            v-model="issued_on"
            value-format="timestamp"
            data-test="invoice_issued_on_date"
            :clearable="false"
            :format="dateFormats.elementUi"
            @change="handleIssuedOnChange"
          />
        </FormGroup>

        <FormGroup
          class="tax-point-on"
          prop="tax_point_on"
          :label="$t('form.invoiceForm.fields.tax_point_on.label')"
          v-show="visibilities.tax_point_on"
          floating-label
          :input-value="tax_point_on"
        >
          <DatePicker
            v-model="tax_point_on"
            value-format="timestamp"
            data-test="invoice_tax_point_on"
            :clearable="false"
            :format="dateFormats.elementUi"
          />
        </FormGroup>

        <FormGroup
          v-if="deliveryDateEnabled"
          class="full-width"
          prop="delivery_on"
          :label="$t('form.invoiceForm.fields.delivery_on.label')"
          floating-label
          :input-value="delivery_on"
        >
          <DatePicker
            v-model="delivery_on"
            value-format="timestamp"
            data-test="invoice_delivery_on"
            :clearable="false"
            :format="dateFormats.elementUi"
          />
        </FormGroup>

        <FormGroup
          class="due-in-form-group"
          prop="due_in"
          :label="$t('form.invoiceForm.fields.due_in.label')"
          v-if="visibilities.due_in"
          floating-label
          :input-value="due_in"
        >
          <div class="due-in-wrapper">
            <Select
              class="select-v2 due-in"
              name="due_in"
              type="date"
              data-test="invoice_due_on"
              v-model="due_in"
              value-key="due_in"
              filterable
              :popper-append-to-body="false"
            >
              <Option
                v-for="option in dueInOptions"
                :key="option.key"
                :label="option.value"
                :value="option.key"
              />
            </Select>
            <div class="el-form-item__help el-form-item__help--due-in">
              <span
                class="due-on-date"
                data-test="due_on_date"
              >
                {{ due_in | daysFromIssuedDate(issued_on, dateFormats.dateFns) }}
              </span>
            </div>
          </div>
        </FormGroup>
        <FormGroup
          prop="due_on_custom"
          :label="$t('form.invoiceForm.fields.due_in.label')"
          v-if="visibilities.due_on_custom"
          floating-label
          :input-value="due_on_custom"
        >
          <DatePicker
            v-model="due_on_custom"
            value-format="timestamp"
            :format="dateFormats.elementUi"
            @change="handleDueOnCustomPick"
          />
        </FormGroup>
        <FormGroup
          class="full-width"
          prop="means_of_payment"
          :label="$t('form.invoiceForm.fields.means_of_payment.label')"
          floating-label
          :input-value="means_of_payment"
        >
          <Select
            class="select-v2"
            name="means_of_payment"
            data-test="payment_method"
            v-model="means_of_payment"
            filterable
            :popper-append-to-body="false"
          >
            <Option
              v-for="option in meansOfPaymentOptions"
              :key="option.key"
              :label="option.value"
              :value="option.key"
              :data-test="'payment_option_' + option.key"
            />
          </Select>
        </FormGroup>

        <InvoiceBankAccount />
      </Column>
    </Row>
    <SequencePopover ref="sequencePopover" />
  </FormSection>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { mapGetters, mapState } from 'vuex';

import { isValid, startOfMonth, isBefore } from 'date-fns';
import { intersection, replace } from 'ramda';

import dateIsAfterDate from '~/lib/validators/dateIsAfterDate';
import { BANK_TRANSFER, CREDIT_CARD, NEW_INVOICE } from '~/config/constants';
import { PATH_INVOICE_SHOW } from '~/router';

import SequencePopover from '~/components/Common/SequencePopover.vue';

import { LAST_INVOICE_QUERY, BACKWARD_NUMBER_QUERY, INVOICE_NUMBER_EXISTS_QUERY } from '~/queries';

const extractNumber = replace(/[^0-9.-]+/g, '');

export default {
  name: 'BasicSettings',
  inject: ['elForm', 'invoiceType'],
  apollo: {
    lastInvoice: {
      query: LAST_INVOICE_QUERY,
      fetchPolicy: 'network-only',
    },
  },
  components: {
    InvoiceBankAccount: () => import('./InvoiceBankAccount.vue'),
    OptionGroup: () => import('~/ui-library/OptionGroup.vue'),
    SequencePopover,
  },
  data() {
    return {
      duplicatedInvoiceId: null,
      initialNumber: '',
      paymentSymbolTimeout: null,
      registrationNumberTimeout: null,
      showConstantSymbol: false,
      showPaymentSymbol: false,
      showRegistrationNumber: false,
      showSpecificSymbol: false,
      specificSymbolEnabled: false,
    };
  },
  computed: {
    ...mapFields('invoiceForm', [
      'constant_symbol',
      'delivery_on',
      'due_in',
      'due_on_custom',
      'issued_by',
      'issued_on',
      'kind',
      'means_of_payment',
      'number',
      'payment_symbol',
      'reference',
      'registration_number',
      'settlement_method',
      'specific_symbol',
      'tax_point_on',
    ]),
    ...mapGetters('invoiceForm', ['visibilities']),
    ...mapGetters('env', ['dateFormats']),
    ...mapGetters('auth', ['isAuthorized']),
    ...mapState('invoiceForm', ['recurring_draft']),
    ...mapState('config', {
      kindOptions: state => state.invoice.kinds,
      settlementMethodOptions: state => state.invoice.settlement_kinds,
      meansOfPaymentOptions: state => state.invoice.means_of_payments,
      dueInOptions: state => state.invoice.due_ins,
      settlementMethodEnabled: state => state.global['settlement_method_enabled?'],
      deliveryDateEnabled: state => state.global['delivery_date_enabled?'],
    }),
    groupedOptions() {
      return this.kindOptions.reduce((groups, option) => {
        const groupIndex = option.key === 'vat_invoice' || option.key === 'invoice' ? 0 : 1;
        groups[groupIndex].options.push({
          key: option.key,
          value: option.value,
        });
        return groups;
      }, [
        { options: [] },
        { options: [] },
      ]);
    },
    showRegistrationNumberToggle() {
      return !this.showRegistrationNumber && this.visibilities.registration_number && !this.settlementMethodEnabled;
    },
    showPaymentSymbolToggle() {
      return !this.showPaymentSymbol && this.visibilities.payment_symbol;
    },
    showConstantSymbolToggle() {
      return !this.showConstantSymbol && this.visibilities.constant_symbol;
    },
    showSpecificSymbolToggle() {
      if (this.specificSymbolEnabled) {
        return !this.showSpecificSymbol && this.visibilities.specific_symbol;
      }
      return false;
    },
    shouldShowComma() {
      return this.showRegistrationNumberToggle || this.showPaymentSymbolToggle;
    },
    numberInputHelp() {
      if (this.lastInvoiceNumber && !this.duplicatedInvoiceId) {
        return this.$i18n.t('form.invoiceForm.fields.number.inputHelp', { lastNumber: this.lastInvoiceNumber });
      }
      return null;
    },
    numberInputError() {
      if (this.duplicatedInvoiceId) {
        return this.$i18n.t('form.invoiceForm.fields.number.inputHelpDuplicated', { link: this.showInvoicePath(this.duplicatedInvoiceId) });
      }
      return null;
    },
    isNewInvoice() {
      return this.invoiceType === NEW_INVOICE;
    },
    lastInvoiceNumber() {
      return this.lastInvoice?.number;
    },
    displayOr() {
      return this.showSpecificSymbolToggle && this.showConstantSymbolToggle;
    },
    displayAlsoNeed() {
      return this.showSpecificSymbolToggle || this.showConstantSymbolToggle;
    },
  },
  methods: {
    showInvoicePath(id) {
      return this.$router.resolve({
        name: PATH_INVOICE_SHOW,
        params: { invoiceId: id },
      }).href;
    },
    checkNumberExistence() {
      if (this.isAuthorized) {
        this.$apollo.query({
          query: INVOICE_NUMBER_EXISTS_QUERY,
          variables: { number: this.number },
        }).then(({ data: { checkInvoiceNumberExistence: { id } } }) => {
          this.duplicatedInvoiceId = id;
        });
      }
    },
    onNumberInput() {
      this.duplicatedInvoiceId = null;
    },
    handleDueOnCustomPick(value) {
      if (value === null) {
        this.$store.dispatch('invoiceForm/getDefaultValue', {
          fieldName: 'due_in',
        });

        this.due_on_custom = new Date();
        this.$store.dispatch('invoiceForm/computeVisibilities');
      }
    },
    toggleField(field) {
      this[field] = !this[field];
      this.$nextTick(() => {
        if (field === 'showRegistrationNumber' && this.showRegistrationNumber) {
          this.$refs.registrationNumberInput.focus();
        } else if (field === 'showPaymentSymbol' && this.showPaymentSymbol) {
          this.$refs.paymentSymbolInput.focus();
        } else if (field === 'showConstantSymbol' && this.showConstantSymbol) {
          this.$refs.constantSymbolInput.focus();
        } else if (field === 'showSpecificSymbol' && this.showSpecificSymbol) {
          this.$refs.specificSymbolInput.focus();
        }
      });
    },
    setRegistrationNumberVisibility() {
      return this.registration_number && this.registration_number !== this.number;
    },
    setPaymentSymbolVisibility() {
      return this.payment_symbol && this.payment_symbol !== extractNumber(this.number);
    },
    setConstantSymbolVisibility() {
      return this.constant_symbol;
    },
    setSpecificSymbolVisibility() {
      return this.specific_symbol;
    },
    onNumberBlur() {
      this.checkNumberExistence();

      if (this.number !== this.initialNumber && this.isAuthorized) {
        this.$refs.sequencePopover.checkPatternForType(this.number, 'invoice', new Date(this.issued_on));
      }

      if (this.number === this.registration_number) return;
      this.$store.dispatch('invoiceForm/updateStoreValue', {
        name: 'registration_number',
        value: this.number,
      });

      this.$store.dispatch('invoiceForm/updateStoreValue', {
        name: 'payment_symbol',
        value: extractNumber(this.number),
      });
    },
    assignReferenceToPopover() {
      this.$refs.sequencePopover.assignReference(this.$refs.popoverReference.$el);
    },
    handleIssuedOnChange(selectedTimestamp) {
      if (this.isAuthorized && !this.recurring_draft) {
        const startOfCurrentMonth = startOfMonth(new Date());
        const selectedDate = new Date(selectedTimestamp);

        if (isBefore(selectedDate, startOfCurrentMonth)) {
          this.$apollo.query({
            query: BACKWARD_NUMBER_QUERY,
            variables: { date: selectedDate },
          }).then(({ data: { detectNumberBackward } }) => {
            if (detectNumberBackward) {
              this.$confirm(this.$i18n.t('form.invoiceForm.backwardDialog.body'), this.$i18n.t('form.invoiceForm.backwardDialog.title'), {
                confirmButtonText: this.$i18n.t('form.invoiceForm.backwardDialog.confirm'),
                cancelButtonText: this.$i18n.t('form.invoiceForm.backwardDialog.cancel'),
                type: 'warning',
                customClass: 'confirm-box',
                confirmButtonClass: 'el-button--warning',
                dangerouslyUseHTMLString: true,
              }).then(() => {
                this.number = detectNumberBackward;

                this.$store.dispatch('invoiceForm/updateStoreValue', {
                  name: 'registration_number',
                  value: detectNumberBackward,
                });

                this.$store.dispatch('invoiceForm/updateStoreValue', {
                  name: 'payment_symbol',
                  value: extractNumber(detectNumberBackward),
                });
              });
            }
          });
        }
      }
    },
    checkSpecificSymbolFeature() {
      this.$posthog?.onFeatureFlags(() => {
        if (this.$posthog?.isFeatureEnabled('specific-symbol-enabled') === true) {
          this.showSpecificSymbol = true;
        }
      });

      if (process.env.NODE_ENV !== 'production') {
        this.showSpecificSymbol = true;
      }
    },
  },
  watch: {
    kind(newVal) {
      if (newVal === 'invoice') {
        this.tax_point_on = this.issued_on;
        this.elForm.validateField('tax_point_on');
      }
      this.$store.dispatch('invoiceForm/computeVisibilities');
    },
    issued_on: {
      handler(newVal) {
        if (!newVal || !isValid(new Date(newVal))) return;

        const shouldTaxPointRecompute = !dateIsAfterDate(this.tax_point_on, newVal);
        if (this.kind === 'invoice' || shouldTaxPointRecompute) {
          this.$store.dispatch('invoiceForm/getDefaultValue', {
            fieldName: 'tax_point_on',
          });
        }

        this.$nextTick(() => {
          if (this.kind === 'invoice') {
            this.elForm.validateField('tax_point_on');
          }
        });
      },
      immediate: true,
    },
    due_in(newVal) {
      if (newVal === 'custom') {
        this.$store.dispatch('invoiceForm/computeVisibilities');
      }
    },
    means_of_payment(...value) {
      if (intersection(value, [BANK_TRANSFER, CREDIT_CARD]).length) {
        this.$store.dispatch('invoiceForm/computeVisibilities');
      }
    },
  },
  mounted() {
    this.assignReferenceToPopover();
    this.checkSpecificSymbolFeature();

    const unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type !== 'invoiceForm/setInvoiceData') return;

      if (this.recurring_draft) {
        this.number = null;
        this.registration_number = null;
      }

      this.showRegistrationNumber = this.recurring_draft || this.setRegistrationNumberVisibility();
      this.showPaymentSymbol = this.recurring_draft || this.setPaymentSymbolVisibility();
      this.showConstantSymbol = this.recurring_draft || this.setConstantSymbolVisibility();
      this.showSpecificSymbol = this.recurring_draft || this.setSpecificSymbolVisibility();

      this.initialNumber = this.number;

      if (this.isNewInvoice) {
        this.checkNumberExistence();
      }

      unsubscribe();
    });
  },
};
</script>

<style lang="scss" scoped>
.el-form-item__help--due-in {
  font-size: rem(14);
  display: flex;

  .due-on-date {
    display: flex;
    align-items: center;
    margin-left: 20px;
  }
}

@media only screen and (max-width: $--breakpoint-sm - 1px) {
  .el-date-editor--date {
    max-width: 100%;
  }
  .el-date-editor.el-input {
    width: 100%;
  }
  .el-form-item--input-narrow .el-input {
    margin-right: 0;
  }
}

.column-right {
  display: flex;
  flex-wrap: wrap;
}

.issued-on, .tax-point-on {
  width: 47%;
}

.tax-point-on {
  margin-left: 6%;
}

.el-date-editor.el-input {
  width: 100%;
}

.row {
  margin-left: 0;
  margin-right: 0;
}

.due-in-wrapper {
  display: flex;
}

.due-in-form-group {
  width: 100%;
  .due-in {
    width: 65%;
  }
}

.full-width {
  width: 100%;
}

.anchor-toggle {
  font-weight: 600;
  margin-left: 2px;
}

.registration-number_label, .payment-symbol_label {
  display: none;
}

@media only screen and (min-width: $--breakpoint-sm) {
  .registration-number_short-label, .payment-symbol_label-short {
    display: none;
  }
  .registration-number_label, .payment-symbol_label {
    display: inline;
  }
}

@media (width >= 990px) {
  .registration-number_short-label, .payment-symbol_label-short {
    display: inline;
  }
  .registration-number_label, .payment-symbol_label {
    display: none;
  }
}

@media (width >= 1100px) {
  .registration-number_short-label, .payment-symbol_label-short {
    display: none;
  }
  .registration-number_label, .payment-symbol_label {
    display: inline;
  }
}

:deep() {
  .input-no-arrows {
    .el-input__suffix {
      background-color: #ffffff;
      top: -10px;
      height: auto;
      padding: 0 2px;
    }
  }
  .optional {
    color: $--color-text-blue;
    font-size: .9rem;
  }
}

.flex-label {
  display: flex;
}

.custom-input-box {
  position: relative;
  top: -25px;
  display: flex;
}

.flex-row {
  display: flex;
  flex-flow: column;
  flex-wrap: wrap;
  justify-content: space-between;

  & > div {
    width: 48%;

    @include media('<xs') {
      width: 100%;
    }
  }
}
</style>
